/**
 * Dashboard Items API
 * Standalone REST API functions for Metabase dashboard management.
 * Uses the app's authToken from localStorage (key: 'authToken').
 */

import { API_CONFIG } from '@/config/envConfig';

const API_BASE = API_CONFIG.BACKEND_BASE;

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export interface MetabaseQuestion {
  id: number;
  name: string;
}

export interface DashboardItem {
  id: number;
  customName: string;
  resourceType?: string;
  resourceId?: number;
  metabaseQuestionId: number;
  metabaseQuestionName: string;
  createdBy?: number;
  createdAt: string;
}

export async function getMetabaseQuestions(userId: number): Promise<MetabaseQuestion[]> {
  const res = await fetch(`${API_BASE}/api/ngpep/${userId}/metabase/questions`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch questions');
  return res.json();
}

export async function getDashboardItems(userId: number): Promise<DashboardItem[]> {
  const res = await fetch(`${API_BASE}/api/ngpep/${userId}/dashboard-items`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch dashboard items');
  return res.json();
}

export async function createDashboardItem(
  userId: number,
  payload: {
    customName: string;
    resourceType: string;
    resourceId: number;
    metabaseQuestionName: string;
  }
): Promise<DashboardItem> {
  const res = await fetch(`${API_BASE}/api/ngpep/${userId}/dashboard-items`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to create dashboard item');
  return res.json();
}

export async function getIframeUrl(
  userId: number,
  itemId: number,
  userEmail: string,
  userType?: string
): Promise<string> {
  const query = new URLSearchParams();
  query.set('user_email', userEmail);
  if (userType) query.set('userType', userType.toUpperCase());

  const res = await fetch(
    `${API_BASE}/api/ngpep/${userId}/dashboard-items/${itemId}/iframe?${query.toString()}`,
    { headers: getAuthHeaders() }
  );
  if (!res.ok) throw new Error('Failed to get iframe URL');
  const data = await res.json();
  return data.iframeUrl;
}
